import { NGXLogger } from 'ngx-logger';
import { Injectable, inject } from '@angular/core';
import { CanActivate, ActivatedRouteSnapshot, RouterStateSnapshot, Router } from '@angular/router';
import { SideBarMenuService } from '../services/side-bar-menu.service';
import { NetworkStatusService } from '../services/network-status.service';
import { ForcedLogoutDialogService } from '../services/forced-logout-dialog.service';

@Injectable({
  providedIn: 'root'
})
export class RoleGuard implements CanActivate {
  private readonly logger = inject(NGXLogger);


  // The JSON string with roles
  userProfile: any;
  constructor(private router: Router,
    private sideBarMenu: SideBarMenuService,
    private networkStatusService: NetworkStatusService,
    private forcedLogoutDialogService: ForcedLogoutDialogService,
  ) { }

  async canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Promise<boolean> {
    try {
      // 2026-09-16: online → decide on the LIVE profile, not the locally cached copy. The cached
      // copy can still carry a role the server no longer grants for this company (real case:
      // dev@lumberlinq.com's old profile carried an ADMIN added for the legacy COMPANY role, so the
      // ADMIN-only Command Center opened while every call on it was refused). The server scopes
      // those roles to the active company now (RoleServiceImpl.scopeAdminToActiveTenant); reading
      // it live makes that take effect on the very next navigation. Offline, or if the live call
      // fails for a non-auth reason, the cached copy is used exactly as before.
      if (this.networkStatusService.isOnline) {
        try {
          this.userProfile = await this.fetchAndCacheUserProfile();
        } catch (liveError: any) {
          if (liveError?.status === 401 || liveError?.error?.code === 'SESSION_INVALIDATED_ELSEWHERE') {
            throw liveError; // handled below exactly as before
          }
          this.logger.warn('[RoleGuard] Live profile fetch failed — using cached profile', liveError);
          this.userProfile = await this.sideBarMenu.getDecryptedUserProfile();
        }
      } else {
        this.userProfile = await this.sideBarMenu.getDecryptedUserProfile();
      }
      if (!this.userProfile) {
        this.userProfile = await this.fetchAndCacheUserProfile();
      }
      this.logger.info('User profile updated successfully in role guard:', JSON.stringify(this.userProfile));
      this.logger.debug(`Checking if user has access to ${route.data['roles']}`);
      const requiredRoles = route.data['roles'] as Array<string>;
      const userRoles = this.userProfile.roles || [];
      const normalizedUserRoles = this.normalizeRoles(userRoles);

      // SUPER_ADMIN/ROOT bypass every specific-role gate here, the same way they already bypass
      // menu_roles filtering server-side (MenuServiceImpl's SUPER_USER_ROLES) — without this, a
      // menu item they can SEE (because menu visibility bypasses for them) could still dead-end
      // on this guard for a role they don't literally hold (e.g. ROLE_AFFILIATE), which is exactly
      // what happened for the Affiliate Portal link. normalizeRole() already strips any "ROLE_"
      // prefix, so this one check covers both raw formats this app's roles show up in.
      const isSuperUser = normalizedUserRoles.includes('SUPER_ADMIN') || normalizedUserRoles.includes('ROOT');

      const hasAccess = isSuperUser || requiredRoles.some(role => {
        const hasRole = normalizedUserRoles.includes(this.normalizeRole(role));
        this.logger.debug(`Role Compare: checking if userRoles includes ${role} - ${hasRole}`);
        return hasRole;
      });
      this.logRoleAccessDebug(requiredRoles, userRoles, hasAccess);

      if (!hasAccess) {
        this.router.navigate(['/unauthorized']); // Navigate to unauthorized page
        return false;
      }

      return true;
    } catch (error) {
      // getDecryptedUserProfile() has no cached profile yet (first navigation this session,
      // nothing in localStorage) → fetchAndCacheUserProfile() hits the network → fails while
      // offline → lands here. Unlike PermissionGuard (already offline-safe), this used to
      // ALWAYS redirect to /unauthorized on any failure, denying access purely because of a
      // connectivity problem, not a real authorization failure — AuthSessionGuard already verified
      // authentication/subscription validity via the offline-grace lease before this guard
      // even runs, so a role check that can't complete offline should fail open, not closed.
      if (!this.networkStatusService.isOnline) {
        this.logger.warn('[RoleGuard] Profile fetch failed while offline — allowing access (AuthSessionGuard is the auth gatekeeper)', error);
        return true;
      }
      // This device's own session was force-revoked by a login elsewhere — show the same
      // dedicated dialog the HTTP interceptor shows for the identical code on any other 401
      // (see ForcedLogoutMarker/JwtRequestFilter), instead of falling through to /unauthorized
      // below, which would wrongly read as "you don't have this role."
      if (error?.error?.code === 'SESSION_INVALIDATED_ELSEWHERE') {
        this.logger.warn('[RoleGuard] Profile fetch 401 traced to a forced-logout-elsewhere marker', error);
        this.forcedLogoutDialogService.open({
          device: error.error?.device || null,
          location: error.error?.location || null,
          kickedAtEpochMillis: error.error?.kickedAtEpochMillis ?? null,
        });
        return false;
      }
      if (error?.status === 401) {
        // Genuine auth failure (dead/invalid session) while online — this used to fall through
        // to /unauthorized unconditionally below, conflating "couldn't verify who you are" with
        // "you don't have this role." Matches AuthSessionGuard's own real-401-vs-network-failure handling
        // for the identical profile-fetch call elsewhere in the app.
        this.logger.error('[RoleGuard] Profile fetch 401 — session invalid, redirecting to login', error);
        this.router.navigate(['/login']);
        return false;
      }
      this.logger.error('Error getting decrypted profile:', error);
      this.router.navigate(['/unauthorized']);
      return false;
    }
  }

  private fetchAndCacheUserProfile(): Promise<any> {
    return new Promise((resolve, reject) => {
      this.sideBarMenu.getUserProfile().subscribe({
        next: (profile: any) => {
          // Attempt to cache but do NOT block on it — if encryption fails the
          // Promise must still resolve so the guard can complete.
          this.sideBarMenu.setEncryptedUserProfile(profile)
            .then(() => resolve(profile))
            .catch(() => resolve(profile)); // encryption failure is non-fatal
        },
        error: reject,
      });
    });
  }

  logRoleAccessDebug(
    requiredRoles: string[],
    userRoles: string[],
    hasAccess: boolean
  ): void {
    this.logger.debug('[ACCESS CHECK START]');
    this.logger.debug('Required Roles:', requiredRoles);
    this.logger.debug('User Roles:', userRoles);

    requiredRoles.forEach(role => {
      const match = userRoles.includes(role);
      this.logger.debug(
        `Checking role "${role}" → ${match ? 'MATCH' : 'NO MATCH'}`
      );
    });

    this.logger.warn(
      `Final Result → ${hasAccess ? 'ACCESS GRANTED' : 'ACCESS DENIED'}`
    );
    this.logger.debug('[ACCESS CHECK END]');
  }

  private normalizeRoles(roles: string[]): string[] {
    return roles.map(role => this.normalizeRole(role));
  }

  private normalizeRole(role: string): string {
    return (role || '').replace(/^ROLE_/, '');
  }

}
